import { useMemo } from "react";
import { useDecisionMetrics, VehicleDecisionData } from "./useDecisionMetrics";

export type RiskSeverity = "high" | "medium" | "low";
export type RiskTimeHorizon = "short" | "medium" | "long";
export type RiskConfidence = "high" | "medium" | "low";

export interface FleetRisk {
  id: string;
  title: string;
  affectedVehicles: { id: string; name: string }[];
  whyItMatters: string;
  evidenceLabel: string;
  evidenceValue: string;
  evidenceDirection: "up" | "down" | "neutral";
  timeHorizon: RiskTimeHorizon;
  confidence: RiskConfidence;
  severity: RiskSeverity;
  severityScore: number; // 0-100 for sorting
  details: {
    metrics: { label: string; value: string }[];
    whatToMonitor: string[];
    possibleActions: string[];
  };
}

export type FleetRiskLevel = "Low" | "Medium" | "High";

export interface FleetRiskSummary {
  score: number; // 0-100
  level: FleetRiskLevel;
}

function vehicleName(v: VehicleDecisionData): string {
  return v.vehicle.name || `${v.vehicle.year} ${v.vehicle.make} ${v.vehicle.model}`;
}

function computeFleetRiskScore(risks: FleetRisk[]): FleetRiskSummary {
  if (risks.length === 0) return { score: 0, level: "Low" };
  const total = risks.reduce((s, r) => s + r.severityScore, 0);
  const score = Math.min(100, Math.round(total / Math.max(risks.length, 1) + risks.length * 5));
  const level: FleetRiskLevel = score >= 60 ? "High" : score >= 30 ? "Medium" : "Low";
  return { score, level };
}

export const useFleetRisks = () => {
  const { vehicleData, fleetAvg, isLoading } = useDecisionMetrics();

  const risks = useMemo<FleetRisk[]>(() => {
    if (!vehicleData.length) return [];
    const result: FleetRisk[] = [];

    // 1. Rising maintenance / expense trend over 3+ months
    const risingExpense = vehicleData.filter(
      (v) => v.monthsActive >= 3 && v.recentExpenseChange > 20 && v.lastMonthExpenses > 50
    );
    if (risingExpense.length > 0) {
      const sorted = [...risingExpense].sort((a, b) => b.recentExpenseChange - a.recentExpenseChange);
      result.push({
        id: "rising-expense-trend",
        title: "Rising Expense Trend",
        affectedVehicles: sorted.map((v) => ({ id: v.vehicle.id, name: vehicleName(v) })),
        whyItMatters: "Persistent cost increases erode profit margins over time, even if revenue stays flat.",
        evidenceLabel: "Avg expense change",
        evidenceValue: `+${Math.round(sorted.reduce((s, v) => s + v.recentExpenseChange, 0) / sorted.length)}%`,
        evidenceDirection: "up",
        timeHorizon: "medium",
        confidence: sorted[0].monthsActive >= 4 ? "high" : "medium",
        severity: sorted[0].recentExpenseChange > 40 ? "high" : "medium",
        severityScore: Math.min(90, Math.round(sorted[0].recentExpenseChange + sorted.length * 5)),
        details: {
          metrics: sorted.slice(0, 3).map((v) => ({
            label: vehicleName(v),
            value: `$${Math.round(v.priorMonthExpenses)} → $${Math.round(v.lastMonthExpenses)} (+${Math.round(v.recentExpenseChange)}%)`,
          })),
          whatToMonitor: [
            "Month-over-month expense totals per vehicle",
            "Maintenance frequency changes",
            "Any new recurring charges",
          ],
          possibleActions: [
            "Audit recent expenses for unusual line items",
            "Compare repair costs against vehicle value",
            "Consider preventive maintenance schedule adjustments",
          ],
        },
      });
    }

    // 2. Profit margin compression
    const compressing = vehicleData.filter(
      (v) => v.monthsActive >= 3 && v.expenseRatio > 60 && v.recentExpenseChange > 10 && v.totalEarnings > 0
    );
    if (compressing.length > 0) {
      result.push({
        id: "margin-compression",
        title: "Profit Margin Compression",
        affectedVehicles: compressing.map((v) => ({ id: v.vehicle.id, name: vehicleName(v) })),
        whyItMatters: "When expenses grow faster than revenue, profitability shrinks even while top-line looks stable.",
        evidenceLabel: "Avg expense ratio",
        evidenceValue: `${Math.round(compressing.reduce((s, v) => s + v.expenseRatio, 0) / compressing.length)}%`,
        evidenceDirection: "up",
        timeHorizon: "medium",
        confidence: compressing.length >= 2 ? "high" : "medium",
        severity: compressing.some((v) => v.expenseRatio > 80) ? "high" : "medium",
        severityScore: Math.round(compressing.reduce((s, v) => s + v.expenseRatio, 0) / compressing.length),
        details: {
          metrics: compressing.slice(0, 3).map((v) => ({
            label: vehicleName(v),
            value: `${Math.round(v.expenseRatio)}% expense ratio, $${Math.round(v.monthlyProfit)}/mo profit`,
          })),
          whatToMonitor: [
            "Expense ratio trend per vehicle",
            "Revenue per trip day vs. cost per trip day",
          ],
          possibleActions: [
            "Review pricing strategy for high-expense vehicles",
            "Evaluate whether certain vehicles are approaching break-even",
          ],
        },
      });
    }

    // 3. Revenue concentration risk
    if (vehicleData.length >= 2) {
      const totalEarnings = vehicleData.reduce((s, v) => s + v.totalEarnings, 0);
      if (totalEarnings > 0) {
        const sorted = [...vehicleData].sort((a, b) => b.totalEarnings - a.totalEarnings);
        const topShare = (sorted[0].totalEarnings / totalEarnings) * 100;
        if (topShare > 50) {
          result.push({
            id: "revenue-concentration",
            title: "Revenue Concentration Risk",
            affectedVehicles: [{ id: sorted[0].vehicle.id, name: vehicleName(sorted[0]) }],
            whyItMatters: "Heavy reliance on a single vehicle means downtime or issues could cause outsized revenue loss.",
            evidenceLabel: "Revenue share",
            evidenceValue: `${Math.round(topShare)}%`,
            evidenceDirection: "neutral",
            timeHorizon: "long",
            confidence: "high",
            severity: topShare > 70 ? "high" : "medium",
            severityScore: Math.round(topShare),
            details: {
              metrics: sorted.slice(0, 3).map((v) => ({
                label: vehicleName(v),
                value: `$${Math.round(v.totalEarnings)} (${Math.round((v.totalEarnings / totalEarnings) * 100)}%)`,
              })),
              whatToMonitor: [
                "Revenue distribution across vehicles",
                "Top earner downtime frequency",
              ],
              possibleActions: [
                "Consider strategies to boost bookings on lower-earning vehicles",
                "Evaluate adding vehicles to diversify revenue",
              ],
            },
          });
        }
      }
    }

    // 4. Declining utilization trend
    const lowUtil = vehicleData.filter(
      (v) => v.monthsActive >= 3 && v.utilization < 30 && v.tripCount > 0
    );
    if (lowUtil.length > 0) {
      const avgUtil = lowUtil.reduce((s, v) => s + v.utilization, 0) / lowUtil.length;
      result.push({
        id: "declining-utilization",
        title: "Low Utilization Pattern",
        affectedVehicles: lowUtil.map((v) => ({ id: v.vehicle.id, name: vehicleName(v) })),
        whyItMatters: "Idle vehicles still incur depreciation, insurance, and storage costs without generating revenue.",
        evidenceLabel: "Avg utilization",
        evidenceValue: `${Math.round(avgUtil)}%`,
        evidenceDirection: "down",
        timeHorizon: "medium",
        confidence: lowUtil.length >= 2 ? "high" : "medium",
        severity: avgUtil < 15 ? "high" : "medium",
        severityScore: Math.round(80 - avgUtil),
        details: {
          metrics: lowUtil.slice(0, 3).map((v) => ({
            label: vehicleName(v),
            value: `${Math.round(v.utilization)}% utilized, ${v.tripDays} trip days over ${v.monthsActive} months`,
          })),
          whatToMonitor: [
            "Monthly trip count trends",
            "Days between bookings",
            "Seasonal patterns in your market",
          ],
          possibleActions: [
            "Review listing pricing and discounts",
            "Evaluate vehicle positioning and photos",
            "Consider market demand in the vehicle's location",
          ],
        },
      });
    }

    // 5. Repeated repair frequency
    const highMaintenance = vehicleData.filter(
      (v) => v.maintenanceCount >= 3 && v.monthsActive >= 2
    );
    if (highMaintenance.length > 0) {
      result.push({
        id: "repair-frequency",
        title: "Repeated Repair Frequency",
        affectedVehicles: highMaintenance.map((v) => ({ id: v.vehicle.id, name: vehicleName(v) })),
        whyItMatters: "Frequent repairs signal potential reliability issues that could escalate in cost and cause booking cancellations.",
        evidenceLabel: "Avg maintenance tasks",
        evidenceValue: `${Math.round(highMaintenance.reduce((s, v) => s + v.maintenanceCount, 0) / highMaintenance.length)} per vehicle`,
        evidenceDirection: "up",
        timeHorizon: "short",
        confidence: "medium",
        severity: highMaintenance.some((v) => v.maintenanceCount >= 5) ? "high" : "medium",
        severityScore: Math.min(85, highMaintenance.reduce((s, v) => s + v.maintenanceCount, 0) * 5),
        details: {
          metrics: highMaintenance.slice(0, 3).map((v) => ({
            label: vehicleName(v),
            value: `${v.maintenanceCount} tasks over ${v.monthsActive} months`,
          })),
          whatToMonitor: [
            "Repair frequency per vehicle per month",
            "Types of recurring maintenance issues",
            "Downtime days due to repairs",
          ],
          possibleActions: [
            "Review maintenance logs for recurring patterns",
            "Evaluate total cost of ownership for high-maintenance vehicles",
            "Consider whether proactive part replacements could reduce frequency",
          ],
        },
      });
    }

    // 6. Cash-drain vehicles (ongoing losses)
    const cashDrains = vehicleData.filter(
      (v) => v.monthlyProfit < 0 && v.monthsActive >= 2 && v.tripCount > 0
    );
    if (cashDrains.length > 0) {
      result.push({
        id: "cash-drain",
        title: "Vehicles Operating at a Loss",
        affectedVehicles: cashDrains.map((v) => ({ id: v.vehicle.id, name: vehicleName(v) })),
        whyItMatters: "Vehicles that consistently lose money consume capital that could be allocated to profitable assets.",
        evidenceLabel: "Monthly loss",
        evidenceValue: `-$${Math.round(Math.abs(cashDrains.reduce((s, v) => s + v.monthlyProfit, 0)))} total`,
        evidenceDirection: "down",
        timeHorizon: "short",
        confidence: cashDrains[0].monthsActive >= 3 ? "high" : "medium",
        severity: cashDrains.some((v) => v.monthlyProfit < -200) ? "high" : "medium",
        severityScore: Math.min(95, Math.round(Math.abs(cashDrains.reduce((s, v) => s + v.monthlyProfit, 0)) / cashDrains.length)),
        details: {
          metrics: cashDrains.slice(0, 3).map((v) => ({
            label: vehicleName(v),
            value: `-$${Math.abs(Math.round(v.monthlyProfit))}/mo over ${v.monthsActive} months`,
          })),
          whatToMonitor: [
            "Monthly profit trajectory",
            "Whether losses are narrowing or widening",
          ],
          possibleActions: [
            "Investigate root cause of losses (high expenses vs. low revenue)",
            "Consider whether repricing or cost reduction is viable",
            "Evaluate opportunity cost of keeping the vehicle",
          ],
        },
      });
    }

    // 7. Increasing expense ratio fleet-wide
    const highExpenseRatioCount = vehicleData.filter((v) => v.expenseRatio > 70 && v.totalEarnings > 0).length;
    if (highExpenseRatioCount >= Math.ceil(vehicleData.length * 0.5) && vehicleData.length >= 2) {
      result.push({
        id: "fleet-expense-ratio",
        title: "Fleet-wide High Expense Ratios",
        affectedVehicles: vehicleData
          .filter((v) => v.expenseRatio > 70)
          .map((v) => ({ id: v.vehicle.id, name: vehicleName(v) })),
        whyItMatters: "When most vehicles have high expense ratios, the fleet's overall profitability is structurally fragile.",
        evidenceLabel: "Vehicles above 70%",
        evidenceValue: `${highExpenseRatioCount} of ${vehicleData.length}`,
        evidenceDirection: "up",
        timeHorizon: "long",
        confidence: "high",
        severity: highExpenseRatioCount >= vehicleData.length * 0.7 ? "high" : "medium",
        severityScore: Math.round((highExpenseRatioCount / vehicleData.length) * 80),
        details: {
          metrics: [
            { label: "Fleet avg expense ratio", value: `${Math.round(fleetAvg.expenseRatio)}%` },
            { label: "Vehicles above 70%", value: `${highExpenseRatioCount}` },
          ],
          whatToMonitor: [
            "Fleet-wide average expense ratio trend",
            "Whether new expenses are one-time or recurring",
          ],
          possibleActions: [
            "Benchmark expenses against similar fleets or vehicles",
            "Identify the largest expense categories across the fleet",
            "Review vendor and service costs for optimization opportunities",
          ],
        },
      });
    }

    return result.sort((a, b) => b.severityScore - a.severityScore);
  }, [vehicleData, fleetAvg]);

  const riskSummary = useMemo(() => computeFleetRiskScore(risks), [risks]);

  return { risks, riskSummary, vehicleData, isLoading };
};
